import classnames from "classnames";
import React from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import WebToAppCampaign from "web/react/components/web-to-app-campaign/web-to-app-campaign";
import { isExperimentRunning } from "web/react/hooks/use-web-to-app-campaign/use-web-to-app-campaign";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import { useProductCardContext } from "./product-card";
import styles from "./product-card-details.css";
import ProductCardLink from "./product-card-link";
import ProductCardLinkKevelEnabled from "./product-card-link-kevel-enabled";
import SeeSimilarInStockButton from "./see-similar-in-stock";

interface ProductCardDetailsProps {
    showVariant?: boolean;
    className?: string;
    feedType?: string;
    analyticsEventLabel?: string;
    hideShortDescription?: boolean | string;
}

const defaultProps = {
    showVariant: false,
};

function ProductCardDetails({
    showVariant,
    className,
    feedType,
    analyticsEventLabel,
    hideShortDescription,
}: ProductCardDetailsProps = defaultProps): React.ReactElement {
    const {
        id: id,
        designer_name: designerName,
        short_description: shortDescription,
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        has_variant: hasVariant,
        in_stock: inStock,
        is_affiliate: isAffiliate,
        show_retailer_as_affiliate: showRetailerAsAffiliate,
        retailer_name: retailerName,
        view_more_url: viewMoreUrl,
    } = useProductCardContext();

    function handleShortDescriptionClick(): void {
        analytics.event("product_card", "short description", analyticsEventLabel);
    }

    function handleAffiliateRetailerClick(): void {
        analytics.event("product_card", "retailer", analyticsEventLabel);
    }

    /* ORG-2888 Creating Similar in stock button for OOS products */
    const oosAnalyticsEventLabel = "see_similar_items_oos";
    const oosEventAction = "see_similar_items_oos_click";
    const enableSimilarItems = environment.getFeature("seo_enable_similar_items_oos_feeds");

    function onCampaignNotActive(): void {
        globals.window.location.href = viewMoreUrl;
    }

    function beforeOnClick(): void {
        analytics.event("product_card", "clicked", oosAnalyticsEventLabel);
    }

    const productCardDescription = (
        <>
            <div className={styles.designer}>{designerName}</div>
            {!hideShortDescription && !!shortDescription && (
                <div className={styles.shortDescription} onClick={handleShortDescriptionClick}>
                    {shortDescription}
                </div>
            )}
        </>
    );

    return (
        <div className={classnames(styles.details, className)}>
            {environment.getFeature("mg_kevel_campaigns") && !isExperimentRunning() ? (
                <ProductCardLinkKevelEnabled
                    reason="text"
                    className={styles.detailsLink}
                    feedType={feedType}
                >
                    {productCardDescription}
                </ProductCardLinkKevelEnabled>
            ) : (
                <ProductCardLink reason="text" className={styles.detailsLink} feedType={feedType}>
                    {productCardDescription}
                </ProductCardLink>
            )}

            <div className={styles.priceSection}>
                {fullPriceWithCurrencySymbol ? (
                    <ProductCardPriceSection />
                ) : (
                    <ProductCardNotAvailable />
                )}
            </div>

            {hasVariant && inStock && showVariant && (
                <p className={styles.variants}>
                    <em>{gettext("product_card.more_colors_available")}</em>
                </p>
            )}

            {showRetailerAsAffiliate && (
                <div className={styles.affiliateRetailer} onClick={handleAffiliateRetailerClick}>
                    <SVGIcon name="hyperlink" />
                    <span>{retailerName}</span>
                </div>
            )}

            {enableSimilarItems && !inStock && (
                <WebToAppCampaign
                    id={id}
                    options={{ action: oosEventAction, isAffiliate: isAffiliate }}
                    onCampaignNotActive={onCampaignNotActive}
                    continueOptionHref={viewMoreUrl}
                    beforeOnClick={beforeOnClick}
                    analyticsEventLabel={oosAnalyticsEventLabel}
                >
                    <SeeSimilarInStockButton
                        className={styles.similarInStock}
                        viewMoreUrl={viewMoreUrl}
                    />
                </WebToAppCampaign>
            )}
        </div>
    );
}

export function ProductCardPriceSection(): React.ReactElement {
    const {
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        sale_price_with_currency_symbol: salePriceWithCurrencySymbol,
        in_stock: inStock,
        free_shipping: freeShipping,
        on_promotion: onPromotion,
        sale_discount: saleDiscount,
        discount_info: discountInfo,
    } = useProductCardContext();

    /* ORG-2888 Creating Similar in stock button for OOS products */
    const enableSimilarItems = environment.getFeature("seo_enable_similar_items_oos_feeds");

    if (!inStock) {
        return (
            <>
                <del
                    className={classnames(styles.price, { [styles.priceGrey]: enableSimilarItems })}
                >
                    {fullPriceWithCurrencySymbol}
                </del>

                <span
                    className={classnames(styles.soldOutMessage, {
                        [styles.soldOutMessageRed]: enableSimilarItems,
                    })}
                >
                    {gettext("product_card.sold_out")}
                </span>
            </>
        );
    }
    return (
        <>
            {freeShipping && <SVGIcon className={styles.icon} name="truck" />}

            {onPromotion && <SVGIcon className={styles.icon} name="tag" />}

            {saleDiscount ? (
                <>
                    <del className={styles.price}>{fullPriceWithCurrencySymbol}</del>
                    <span className={classnames(styles.price, styles.salePrice)}>
                        {salePriceWithCurrencySymbol}
                    </span>
                </>
            ) : (
                <span className={styles.price}>{fullPriceWithCurrencySymbol}</span>
            )}

            {discountInfo && <span className={styles.discountInfo}>{discountInfo}</span>}
        </>
    );
}

export function ProductCardNotAvailable(): React.ReactElement {
    return (
        <span className={styles.soldOutMessage}>
            {gettext("product_card.not_available_in_your_country")}
        </span>
    );
}

export default ProductCardDetails;
